/**
 * Projects API service for managing projects.
 * @module lib/api/projects.api
 */

import { track, trackActivationMilestone } from '@/lib/analytics'

import type {
  CreateFromTemplateOptions,
  CreateProject,
  ExportOptions,
  ImportOptions,
  ImportPreviewRecord,
  ImportResult,
  Project,
  ProjectPortalVisibility,
  ProjectStatusEnum,
  ProjectWithEntity,
  SaveAsTemplateOptions,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

// Response types
interface ProjectsResponse {
  data: ProjectWithEntity[]
  meta: {
    total: number
    skip: number
    take: number
  }
}

interface CodeAvailabilityResponse {
  available: boolean
}

// Filter types
interface ProjectFilters {
  name?: string
  status?: ProjectStatusEnum | string
  includeCustomFields?: boolean
  moduleType?: string
  moduleId?: string
  entityId?: string
  dealId?: string
  columnFilters?: Record<string, unknown> | string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  isTemplate?: boolean
  /** ScQL query string — translated server-side by GET /projects?query= */
  query?: string
}

interface ModuleCountsResponse {
  allProjects: number
  templates: number
  [key: string]: number // Index signature to satisfy base class
}

interface CreateFromTemplateRequest {
  name: string
  code?: string
  description?: string
  moduleType?: string
  moduleId?: string
  startDate?: string
  endDate?: string
  cycleStartDate?: string
  options?: CreateFromTemplateOptions
}

interface GetProjectOptions {
  signal?: AbortSignal
  includeCustomFields?: boolean
}

export interface BulkAssignProjectMembersRequest {
  projectIds: string[]
  userIds: string[]
  role: string
}

export interface BulkAssignProjectMembersResponse {
  createdCount: number
  updatedCount: number
  unchangedCount: number
  totalCount: number
}

export interface MergeProjectsRequest {
  projectIds: string[]
  primaryProjectId: string
  confirmation: 'MERGE'
}

export interface MergeProjectsResponse {
  primaryProjectId: string
  deletedProjectIds: string[]
  counts: Record<string, number>
}

export type LinkedItemAction = 'reassign' | 'unlink' | 'delete'

export interface LinkedItemsSummary {
  tasks: number
  notes: number
  documents: number
  discussions: number
}

export interface DeleteProjectOptions {
  tasks?: LinkedItemAction
  notes?: LinkedItemAction
  documents?: LinkedItemAction
  discussions?: LinkedItemAction
  targetProjectId?: string
}

export interface ProjectWatchersSummary {
  count: number
  isWatching: boolean
  watchers: Array<{
    userId: string
    displayName: string | null
    email: string
    image: string | null
  }>
}

/**
 * Transform project data to flatten custom fields from nested object to top-level with custom_ prefix
 */
const flattenProjectCustomFields = <T extends ProjectWithEntity | Project>(
  project: T,
): T => {
  const result = { ...project }

  // Flatten custom fields from nested customFields object to top-level with custom_ prefix
  if (result.customFields && typeof result.customFields === 'object') {
    Object.entries(result.customFields).forEach(([fieldName, value]) => {
      const flattenedKey = `custom_${fieldName}`
      ;(result as Record<string, unknown>)[flattenedKey] = value
    })
  }

  return result
}

class ProjectsApiService extends BaseApi<
  ProjectWithEntity,
  CreateProject,
  Partial<ProjectWithEntity>
> {
  constructor() {
    super({
      baseEndpoint: '/projects',
      responseNormalization: true,
    })
  }

  // Get all projects with optional filters
  async getProjects(
    filters?: ProjectFilters,
    request?: { signal?: AbortSignal },
  ): Promise<ProjectsResponse> {
    const normalizedFilters = { ...(filters || {}) } as Record<string, unknown>
    const columnFilters = normalizedFilters.columnFilters

    if (
      columnFilters &&
      typeof columnFilters === 'object' &&
      !Array.isArray(columnFilters)
    ) {
      normalizedFilters.columnFilters =
        Object.keys(columnFilters).length > 0
          ? JSON.stringify(columnFilters)
          : undefined
    }

    const queryParams = this.buildQueryParams(normalizedFilters)
    const url = `${this.config.baseEndpoint}${queryParams}`
    const response = await this.makeRequest<ProjectsResponse>(url, request)
    // Flatten custom fields for each project
    return {
      ...response,
      data: response.data.map(flattenProjectCustomFields),
    }
  }

  // Get projects for a specific module (entity, lead, deal, etc.)
  async getProjectsByModule(
    moduleType: string,
    moduleId: string,
    filters?: { status?: ProjectStatusEnum | string; limit?: number },
    request?: { signal?: AbortSignal },
  ): Promise<ProjectsResponse> {
    return this.getProjects(
      {
        moduleType,
        moduleId,
        status: filters?.status,
        includeCustomFields: true,
        limit: filters?.limit ?? 500,
      },
      request,
    )
  }

  // Get projects for a specific entity
  async getProjectsByEntityId(
    entityId: string,
    filters?: { status?: ProjectStatusEnum | string; limit?: number },
    request?: { signal?: AbortSignal },
  ): Promise<ProjectsResponse> {
    return this.getProjectsByModule('ENTITY', entityId, filters, request)
  }

  // Compatibility wrapper for old lead callers. Lead ids resolve to migrated
  // contact ids; LEAD module rows are now contact-backed.
  async getProjectsByLeadId(
    leadId: string,
    filters?: { status?: ProjectStatusEnum | string; limit?: number },
  ): Promise<ProjectsResponse> {
    const resolved = await this.makeRequest<{ contactId: string | null }>(
      `/contacts/resolve-lead/${leadId}`,
    )
    if (!resolved.contactId) {
      return {
        data: [],
        meta: { total: 0, skip: 0, take: filters?.limit ?? 500 },
      }
    }
    return this.getProjectsByModule('LEAD', resolved.contactId, filters)
  }

  // Get projects for a specific deal
  async getProjectsByDealId(
    dealId: string,
    filters?: { status?: ProjectStatusEnum | string },
  ): Promise<ProjectsResponse> {
    return this.getProjectsByModule('DEAL', dealId, filters)
  }

  // Get a single project by ID
  async getProject(
    id: string,
    options?: GetProjectOptions,
  ): Promise<ProjectWithEntity> {
    let url = `${this.config.baseEndpoint}/${id}`
    if (options?.includeCustomFields) {
      url += '?includeCustomFields=true'
    }
    const project = await this.makeRequest<ProjectWithEntity>(url, {
      signal: options?.signal,
    })
    return flattenProjectCustomFields(project)
  }

  // Create a new project
  async createProject(project: CreateProject): Promise<ProjectWithEntity> {
    const created = await this.makeRequest<ProjectWithEntity>(
      this.config.baseEndpoint,
      {
        method: 'POST',
        body: project,
      },
    )

    // Blank-project path. Template instantiation is a separate endpoint and
    // reports itself (see templates.api.ts).
    track('project_created', {
      source: 'blank',
      has_client: !!project.entityId,
    })
    trackActivationMilestone('project_created', { source: 'blank' })

    return created
  }

  // Update an existing project
  async updateProject(
    id: string,
    project: Partial<ProjectWithEntity>,
  ): Promise<ProjectWithEntity> {
    return this.makeRequest<ProjectWithEntity>(
      `${this.config.baseEndpoint}/${id}`,
      {
        method: 'PATCH',
        body: project,
      },
    )
  }

  async bulkAssignMembers(
    data: BulkAssignProjectMembersRequest,
  ): Promise<BulkAssignProjectMembersResponse> {
    return this.makeRequest<BulkAssignProjectMembersResponse>(
      `${this.config.baseEndpoint}/bulk-members`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async mergeProjects(
    data: MergeProjectsRequest,
  ): Promise<MergeProjectsResponse> {
    return this.makeRequest<MergeProjectsResponse>(
      `${this.config.baseEndpoint}/merge`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  // Archive a project (status → 'archived'; hidden from lists, tasks hidden
  // from cross-project views; data intact)
  async archiveProject(id: string): Promise<ProjectWithEntity> {
    return this.makeRequest<ProjectWithEntity>(
      `${this.config.baseEndpoint}/${id}/archive`,
      { method: 'POST' },
    )
  }

  // Unarchive a project (restores its pre-archive status)
  async unarchiveProject(id: string): Promise<ProjectWithEntity> {
    return this.makeRequest<ProjectWithEntity>(
      `${this.config.baseEndpoint}/${id}/unarchive`,
      { method: 'POST' },
    )
  }

  // Project watchers: who watches this project + whether the current user does
  async getProjectWatchers(id: string): Promise<ProjectWatchersSummary> {
    return this.makeRequest<ProjectWatchersSummary>(
      `${this.config.baseEndpoint}/${id}/watchers`,
    )
  }

  // Watch a project (self-service, requires the Projects - Watch permission)
  async watchProject(id: string): Promise<{ isWatching: boolean }> {
    return this.makeRequest<{ isWatching: boolean }>(
      `${this.config.baseEndpoint}/${id}/watchers`,
      { method: 'POST' },
    )
  }

  // Stop watching a project
  async unwatchProject(id: string): Promise<{ isWatching: boolean }> {
    return this.makeRequest<{ isWatching: boolean }>(
      `${this.config.baseEndpoint}/${id}/watchers`,
      { method: 'DELETE' },
    )
  }

  // Get linked items summary for deletion dialog
  async getLinkedItemsSummary(
    id: string,
    request?: { signal?: AbortSignal },
  ): Promise<LinkedItemsSummary> {
    return this.makeRequest<LinkedItemsSummary>(
      `${this.config.baseEndpoint}/${id}/linked-items-summary`,
      request,
    )
  }

  // Delete a project with optional linked item handling
  async deleteProject(
    id: string,
    options?: DeleteProjectOptions,
  ): Promise<void> {
    await this.makeRequest(`${this.config.baseEndpoint}/${id}`, {
      method: 'DELETE',
      ...(options ? { body: options } : {}),
    })
  }

  // Check if a project code is available
  async checkCodeAvailability(code: string): Promise<CodeAvailabilityResponse> {
    const encodedCode = encodeURIComponent(code)
    return this.makeRequest<CodeAvailabilityResponse>(
      `${this.config.baseEndpoint}/check-code/${encodedCode}`,
    )
  }

  // Check if a project code prefix is available
  async checkCodePrefixAvailability(
    prefix: string,
  ): Promise<CodeAvailabilityResponse> {
    const encodedPrefix = encodeURIComponent(prefix)
    return this.makeRequest<CodeAvailabilityResponse>(
      `${this.config.baseEndpoint}/check-code-prefix/${encodedPrefix}`,
    )
  }

  // Import/Export operations
  async previewImport(
    records: unknown[],
    options: ImportOptions,
  ): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(
      `${this.config.baseEndpoint}/import/preview`,
      {
        method: 'POST',
        body: { records, options },
      },
    )
  }

  async import(data: {
    records: ImportPreviewRecord[]
    options: ImportOptions
  }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(
      `${this.config.baseEndpoint}/import`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async export(options: ExportOptions): Promise<Project[]> {
    return this.makeRequest<Project[]>(`${this.config.baseEndpoint}/export`, {
      method: 'POST',
      body: options,
    })
  }

  // Logo management
  async uploadLogo(
    projectId: string,
    file: File,
  ): Promise<{ logoUrl: string }> {
    const formData = new FormData()
    formData.append('file', file)
    return this.makeRequest<{ logoUrl: string }>(
      `${this.config.baseEndpoint}/${projectId}/logo`,
      {
        method: 'POST',
        body: formData,
      },
    )
  }

  async getLogoUrl(projectId: string): Promise<{ logoUrl: string | null }> {
    return this.makeRequest<{ logoUrl: string | null }>(
      `${this.config.baseEndpoint}/${projectId}/logo`,
    )
  }

  async bulkGetLogoUrls(
    projectIds: string[],
  ): Promise<Record<string, string | null>> {
    const res = await this.makeRequest<{
      logoUrls: Record<string, string | null>
    }>(`${this.config.baseEndpoint}/logos/bulk`, {
      method: 'POST',
      body: { projectIds },
    })
    return res.logoUrls
  }

  async removeLogo(projectId: string): Promise<Project> {
    return this.makeRequest<Project>(
      `${this.config.baseEndpoint}/${projectId}/logo`,
      {
        method: 'DELETE',
      },
    )
  }

  // Template operations

  // Get module counts (projects and templates)
  async getModuleCounts(): Promise<ModuleCountsResponse> {
    return this.makeRequest<ModuleCountsResponse>(
      `${this.config.baseEndpoint}/module-counts`,
    )
  }

  async getCreateCounts(): Promise<ModuleCountsResponse> {
    return this.makeRequest<ModuleCountsResponse>(
      `${this.config.baseEndpoint}/create-counts`,
    )
  }

  // Get all templates
  async getTemplates(
    filters?: Omit<ProjectFilters, 'isTemplate'>,
  ): Promise<ProjectsResponse> {
    return this.getProjects({
      ...filters,
      isTemplate: true,
    })
  }

  async getAvailableTemplates(
    filters?: Omit<ProjectFilters, 'isTemplate'>,
  ): Promise<ProjectsResponse> {
    const queryParams = this.buildQueryParams(
      (filters || {}) as Record<string, unknown>,
    )
    const response = await this.makeRequest<ProjectsResponse>(
      `${this.config.baseEndpoint}/available-templates${queryParams}`,
    )
    return {
      ...response,
      data: response.data.map(flattenProjectCustomFields),
    }
  }

  // Save an existing project as a template
  async saveAsTemplate(
    projectId: string,
    options: SaveAsTemplateOptions,
  ): Promise<ProjectWithEntity> {
    return this.makeRequest<ProjectWithEntity>(
      `${this.config.baseEndpoint}/${projectId}/save-as-template`,
      {
        method: 'POST',
        body: options,
      },
    )
  }

  // Staff-side summary of what the project's published portal share exposes
  async getClientPortalVisibility(
    projectId: string,
  ): Promise<ProjectPortalVisibility> {
    return this.makeRequest<ProjectPortalVisibility>(
      `${this.config.baseEndpoint}/${projectId}/client-portal-visibility`,
    )
  }

  // Create a new project from a template
  async createFromTemplate(
    templateId: string,
    data: CreateFromTemplateRequest,
  ): Promise<ProjectWithEntity> {
    return this.makeRequest<ProjectWithEntity>(
      `${this.config.baseEndpoint}/from-template/${templateId}`,
      {
        method: 'POST',
        body: data,
      },
    )
  }
}

// Export singleton instance
export const projectsApi = createSingletonApi(ProjectsApiService)
